<template>
  <div>
    <Copy
      v-if="statusCode === 401"
      size="m"
      color="supportDanger"
      centered
      bold
      responsive
      :staticClass="`${el}__personalDataFormError`"
    >
      {{ $t('profile.forms.error.generics') }}
    </Copy>

    <div v-if="editMode">
      <form @submit.prevent="handleSubmit">
        <Row>
          <Col :xs="12" :m="6" hasMargin>
            <BaseInput
              :label="$t('profile.personalData.form.label.firstname')"
              name="firstname"
              v-model.trim="formFields.firstname.value"
              :error="formFields.firstname.error"
              @input="clearError(formFields.firstname)"
              clearable
            />
          </Col>
          <Col :xs="12" :m="6" hasMargin>
            <BaseInput
              :label="$t('profile.personalData.form.label.lastname')"
              name="lastname"
              v-model.trim="formFields.lastname.value"
              :error="formFields.lastname.error"
              @input="clearError(formFields.lastname)"
              clearable
            />
          </Col>
        </Row>

        <Row>
          <Col :xs="12" :m="9" hasMargin>
            <BaseInput
              :label="$t('profile.personalData.form.label.street')"
              name="street"
              v-model.trim="formFields.street.value"
              :error="formFields.street.error"
              @input="clearError(formFields.street)"
              clearable
            />
          </Col>
          <Col :xs="12" :m="3" hasMargin>
            <BaseInput
              :label="$t('profile.personalData.form.label.houseNumber')"
              name="houseNumber"
              v-model.trim="formFields.houseNumber.value"
              :error="formFields.houseNumber.error"
              @input="clearError(formFields.houseNumber)"
              clearable
            />
          </Col>
        </Row>

        <Row>
          <Col :xs="12" :m="4" hasMargin>
            <BaseInput
              :label="$t('profile.personalData.form.label.zipCode')"
              name="zipCode"
              v-model.trim="formFields.zipCode.value"
              :error="formFields.zipCode.error"
              @input="clearError(formFields.zipCode)"
              clearable
            />
          </Col>
          <Col :xs="12" :m="8" hasMargin>
            <BaseInput
              :label="$t('profile.personalData.form.label.city')"
              name="city"
              v-model.trim="formFields.city.value"
              :error="formFields.city.error"
              @input="clearError(formFields.city)"
              clearable
            />
          </Col>
        </Row>

        <div :class="`${el}__buttonRow`">
          <Button
            :label="$t('profile.button.label.cancel')"
            type="outlined"
            @click="changeEditMode"
            small
            :expand="!$matchMedia.m"
          />
          <Button
            :label="$t('profile.button.label.save')"
            type="primary"
            :loading="loadForm"
            submit
            small
            :expand="!$matchMedia.m"
          />
        </div>
      </form>
    </div>

    <Copy element="div" v-else :staticClass="`${el}__personalDataAddressField`">
      <div :class="`${el}__initialsCircle`">
        {{ initials }}
      </div>

      <div v-if="loadData" :class="`${el}__personalDataAddressFieldSpinner`">
        <Col hasMargin center>
          <Spinner />
        </Col>
      </div>
      <template v-else>
        <Row>
          <Col :xs="12" :l="4">
            <div>
              <Copy element="div" size="s" color="tertiaryLight">
                {{ $t('profile.personalData.form.label.firstname') + ':' }}
              </Copy>
              <Copy element="div" size="l" bold>
                {{ this.user.firstname }}
              </Copy>
            </div>
            <div class="u-margin__top--2">
              <Copy element="div" size="s" color="tertiaryLight">
                {{ $t('profile.personalData.form.label.lastname') + ':' }}
              </Copy>

              <Copy element="div" size="l" bold>
                {{ this.user.lastname }}
              </Copy>
            </div>
          </Col>
          <Col :xs="12" :l="4">
            <div class="u-margin__top--2@l-down">
              <Copy element="div" size="s" color="tertiaryLight">
                {{ $t('profile.personalData.form.label.street') + ':' }}
              </Copy>

              <Copy element="div" size="l" bold>
                {{ formFields.street.value || '-' }}
                {{ formFields.houseNumber.value }}
              </Copy>
            </div>

            <Row>
              <Col>
                <div class="u-margin__top--2">
                  <Copy element="div" size="s" color="tertiaryLight">
                    {{ $t('profile.personalData.form.label.zipCode') + ':' }}
                  </Copy>
                  <Copy element="div" size="l" bold>
                    {{ formFields.zipCode.value || '-' }}
                  </Copy>
                </div>
              </Col>
              <Col>
                <div class="u-margin__top--2">
                  <Copy element="div" size="s" color="tertiaryLight">
                    {{ $t('profile.personalData.form.label.city') + ':' }}
                  </Copy>
                  <Copy element="div" size="l" bold>
                    {{ formFields.city.value || '-' }}
                  </Copy>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        <Link icon="pencil-line" noUnderline @click="changeEditMode" class="u-margin__top--3">
          Bearbeiten
        </Link>
      </template>
    </Copy>
  </div>
</template>

<script>
import Vue from 'vue';
import Copy from '@/objects/Copy';
import Spinner from '@/objects/Spinner';
import Col from '@/components/Grid/Col';
import Button from '@/objects/Button';
import Row from '@/components/Grid/Row';
import BaseInput from '@/objects/Forms/BaseInput';
import Link from '@/objects/Link';
import { validateForm } from '@/helpers/formValidation';
import initialsMixin from '@/mixins/initials';
import { mapFieldsHealth } from '@/store/mapFields';

import {
  namePattern,
  streetPattern,
  houseNumberPattern,
  zipCodePattern,
  cityPattern,
} from '@/helpers/patterns';
import { USER_CHANGE_REQUEST } from '@/store/actions/user';
import {
  ROUTE_API_GET_PROFILE,
  ROUTE_API_CHANGE_PROFILE,
  ROUTE_API_CHANGE_USERDATA,
} from '@/constants/apiRoutes';
import { createValidationData } from '@/helpers/tracking/validationTracking';

export default {
  name: 'PersonalDataForm',
  components: { Copy, Spinner, Link, BaseInput, Row, Button, Col },
  props: {
    el: String,
  },
  mixins: [initialsMixin],
  mounted() {
    this.getProfileData();
  },

  computed: {
    ...mapFieldsHealth(['dependentPerson', 'healthUuid']),
  },

  data() {
    return {
      loadData: false,
      loadForm: false,
      statusCode: '',
      editMode: false,
      formFields: {
        firstname: {
          value: '',
          error: null,
          required: this.$t('profile.personalData.form.error.firstname.required'),
          pattern: {
            value: namePattern,
            message: this.$t('profile.personalData.form.error.firstname.formatted'),
          },
        },
        lastname: {
          value: '',
          error: null,
          required: this.$t('profile.personalData.form.error.lastname.required'),
          pattern: {
            value: namePattern,
            message: this.$t('profile.personalData.form.error.lastname.formatted'),
          },
        },
        street: {
          value: '',
          error: null,
          required: false,
          pattern: {
            value: streetPattern,
            message: this.$t('profile.personalData.form.error.street.formatted'),
          },
        },
        houseNumber: {
          value: '',
          error: null,
          required: false,
          pattern: {
            value: houseNumberPattern,
            message: this.$t('profile.personalData.form.error.houseNumber.formatted'),
          },
        },
        zipCode: {
          value: '',
          error: null,
          required: false,
          pattern: {
            value: zipCodePattern,
            message: this.$t('profile.personalData.form.error.zipCode.formatted'),
          },
        },
        city: {
          value: '',
          error: null,
          required: false,
          pattern: {
            value: cityPattern,
            message: this.$t('profile.personalData.form.error.city.formatted'),
          },
        },
      },
    };
  },
  methods: {
    handleSubmit(e) {
      this.loadForm = true;
      const { isValid } = validateForm(e.target.elements, this.formFields);

      if (isValid) {
        let axiosData;
        const address = {
          street: this.formFields.street.value,
          houseNumber: this.formFields.houseNumber.value,
          city: this.formFields.city.value,
          zipCode: this.formFields.zipCode.value,
        };

        const user = {
          firstname: this.formFields.firstname.value,
          lastname: this.formFields.lastname.value,
        };

        axiosData = [
          {
            url: ROUTE_API_CHANGE_PROFILE,
            data: { profile: address, careProfile: user, personInNeedOfCareUuid: this.healthUuid },
          },
        ];

        if (user.firstname !== this.user.firstname || user.lastname !== this.user.lastname) {
          axiosData.push({ url: ROUTE_API_CHANGE_USERDATA, data: user });
        }

        Promise.all(axiosData.map((data) => Vue.axios.post(data.url, data.data)))
          .then((response) => {
            this.statusCode = response.status;
            this.changeEditMode();
            if (user.firstname !== this.user.firstname || user.lastname !== this.user.lastname) {
              this.$store.dispatch(USER_CHANGE_REQUEST, user);
            }
            this.loadForm = false;
          })
          .catch((error) => {
            // Error-Codes
            // 401: something went wrong
            // 422: validation error

            if (error.response) {
              const {
                data: { errors },
                status,
              } = error.response;

              this.statusCode = status;

              if (status === 422) {
                Object.keys(errors).map((error) => {
                  const type = errors[error][0];
                  this.formFields[error].error = this.$t(
                    `profile.personalData.form.error.${error}.${type}`
                  );
                });
              }
            } else {
              this.statusCode = 401;
            }
            this.loadForm = false;
          });
      } else {
        createValidationData(this.formFields);
        this.loadForm = false;
      }
    },

    changeEditMode() {
      this.editMode = !this.editMode;
      this.getProfileData();
    },

    getProfileData() {
      this.loadData = true;
      Vue.axios
        .get(ROUTE_API_GET_PROFILE)
        .then((response) => {
          this.statusCode = response.status;
          this.setProfileData(response.data.profile);
          this.loadData = false;
        })
        .catch(() => {
          this.statusCode = 401;
        });
    },

    setProfileData(data) {
      this.formFields.firstname.value = this.user.firstname;
      this.formFields.lastname.value = this.user.lastname;
      this.formFields.street.value = data.street ? data.street : '';
      this.formFields.houseNumber.value = data.houseNumber ? data.houseNumber : '';
      this.formFields.zipCode.value = data.zipCode ? data.zipCode : '';
      this.formFields.city.value = data.city ? data.city : '';
    },

    clearError(field) {
      field.error = null;
    },
  },
};
</script>
